import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { playerAPI } from '../services/api';
import { invalidatePlayerNotificationQueries } from '../utils/playerNotificationQueries';

const BRAND = { blue: '#043663', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  min-height: 400px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(4, 54, 99, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const InboxSidebar = styled.div`
  width: 100%;
  border-bottom: 1px solid ${BRAND.gray};
  display: flex;
  flex-direction: column;
  min-height: 200px;

  @media (min-width: 768px) {
    width: 320px;
    min-width: 280px;
    border-bottom: none;
    border-right: 1px solid ${BRAND.gray};
  }
`;

const InboxHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${BRAND.gray};
  flex-shrink: 0;
`;
const InboxTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${BRAND.gray};
  font-size: 0.875rem;
  &::placeholder {
    color: #9ca3af;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
`;
const TabRow = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${BRAND.gray};
  padding: 0 0.25rem;
  flex-shrink: 0;
`;
const Tab = styled.button`
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(p) => (p.$active ? BRAND.blue : '#64748b')};
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? BRAND.blue : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: ${(p) => (p.$active ? BRAND.blue : '#0f172a')};
  }
`;
const ConversationItem = styled.button`
  width: 100%;
  padding: 1rem 1.25rem;
  border: none;
  border-bottom: 1px solid ${BRAND.gray};
  background: ${(p) => (p.$active ? BRAND.lightBg : '#fff')};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${BRAND.lightBg};
  }
`;
const ConvName = styled.div`
  font-weight: 600;
  color: #0f172a;
  font-size: 0.9375rem;
`;
const ConvPreview = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ConvTime = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;
`;
const ConvRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`;
const ConvMeta = styled.div`
  flex: 1;
  min-width: 0;
`;
const UnreadBadge = styled.span`
  flex-shrink: 0;
  background: #D52B1E;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 10px;
  min-width: 1.25rem;
  text-align: center;
`;

const HideThreadBtn = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    color: #b91c1c;
    background: #fef2f2;
  }
`;

const ChatHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ChatPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  background: #fafafa;

  @media (min-width: 768px) {
    min-height: 0;
  }
`;

const ChatHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${BRAND.gray};
  background: #fff;
  flex-shrink: 0;
`;
const ChatTitle = styled.div`
  font-weight: 600;
  color: #0f172a;
  font-size: 1rem;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;
const MessageBubble = styled.div`
  max-width: min(92%, 42rem);
  width: fit-content;
  align-self: ${(p) => (p.$isMe ? 'flex-end' : 'flex-start')};
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  background: ${(p) => (p.$isMe ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$isMe ? '#fff' : '#0f172a')};
  font-size: 0.9375rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;
const MessageTime = styled.div`
  font-size: 0.7rem;
  color: ${(p) => (p.$isMe ? 'rgba(255,255,255,0.8)' : '#94a3b8')};
  margin-top: 0.2rem;
`;

const ChatForm = styled.form`
  padding: 1rem;
  border-top: 1px solid ${BRAND.gray};
  background: #fff;
  display: flex;
  gap: 0.5rem;
`;
const ChatInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${BRAND.gray};
  font-size: 0.9375rem;
`;
const SendBtn = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  background: ${BRAND.blue};
  color: #fff;
  border: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9375rem;
`;
const ReadOnlyNotice = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid ${BRAND.gray};
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.8125rem;
  text-align: center;
`;

const LoadingBlock = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #64748b;
  font-size: 0.9375rem;
`;

export default function PlayerMessagesPage() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [body, setBody] = useState('');
  const [activeTab, setActiveTab] = useState('ongoing'); // 'ongoing' | 'completed'
  const bottomRef = useRef(null);

  const { data: conversationsRes, isLoading: conversationsLoading } = useQuery(
    ['playerConversations'],
    () => playerAPI.getConversations(),
    { staleTime: 30 * 1000, refetchOnWindowFocus: true }
  );
  const conversations = conversationsRes?.data ?? [];
  const filtered = search.trim()
    ? conversations.filter((c) => (c.otherPlayer?.name || '').toLowerCase().includes(search.trim().toLowerCase()))
    : conversations;
  const ongoing = filtered.filter((c) => !c.isCompleted);
  const completed = filtered.filter((c) => c.isCompleted);
  const listForTab = activeTab === 'ongoing' ? ongoing : completed;
  const activeConv = conversations.find((c) => c.id === conversationId);
  const isActiveCompleted = activeConv?.isCompleted === true;

  const { data: messagesRes, refetch: refetchMessages, isLoading: messagesLoading } = useQuery(
    ['conversationMessages', conversationId],
    () => playerAPI.getConversationMessages(conversationId),
    { enabled: !!conversationId, refetchInterval: 5000 }
  );
  const messages = messagesRes?.data ?? [];

  const sendMessage = useMutation(
    () => playerAPI.sendMessage(conversationId, body),
    {
      onSuccess: () => {
        setBody('');
        refetchMessages();
        queryClient.invalidateQueries(['playerConversations']);
      }
    }
  );

  const hideConversation = useMutation(
    (id) => playerAPI.hideConversation(id),
    {
      onSuccess: (_res, id) => {
        queryClient.invalidateQueries(['playerConversations']);
        invalidatePlayerNotificationQueries(queryClient);
        if (conversationId && String(conversationId) === String(id)) {
          navigate('/player/messages');
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('playerMessagesPage.hideThreadFailed'));
      },
    }
  );

  const handleHideThread = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    if (!id || hideConversation.isLoading) return;
    if (!window.confirm(t('playerMessagesPage.hideThreadConfirm'))) return;
    hideConversation.mutate(id);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark that user is viewing this conversation (so new messages don't create unread notification)
  useEffect(() => {
    if (!conversationId) return;
    playerAPI.postConversationViewing(conversationId).catch(() => {});
    playerAPI.markNotificationsReadByConversation(conversationId).then(() => {
      queryClient.invalidateQueries(['playerNotificationCount']);
      queryClient.invalidateQueries(['playerNotificationCount', 'messages']);
      queryClient.invalidateQueries(['playerNotificationsUnreadSummary']);
      queryClient.invalidateQueries(['playerNotifications']);
      queryClient.invalidateQueries(['playerConversations']);
    }).catch(() => {});
    const interval = setInterval(() => {
      playerAPI.postConversationViewing(conversationId).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [conversationId, queryClient]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!body.trim() || !conversationId) return;
    sendMessage.mutate();
  };

  return (
    <PageWrap>
      <InboxSidebar>
        <InboxHeader>
          <InboxTitle>{t('playerMessagesPage.title')}</InboxTitle>
          <SearchInput
            type="text"
            placeholder={t('playerMessagesPage.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TabRow>
            <Tab
              type="button"
              $active={activeTab === 'ongoing'}
              onClick={() => setActiveTab('ongoing')}
            >
              {t('playerMessagesPage.tabOngoing')}
            </Tab>
            <Tab
              type="button"
              $active={activeTab === 'completed'}
              onClick={() => setActiveTab('completed')}
            >
              {t('playerMessagesPage.tabCompleted')}
            </Tab>
          </TabRow>
        </InboxHeader>
        <ConversationList>
          {conversationsLoading && (
            <LoadingBlock>
              {t('playerMessagesPage.loadingConversations')}
            </LoadingBlock>
          )}
          {!conversationsLoading && listForTab.length === 0 && (
            <EmptyState style={{ padding: '1.5rem' }}>
              {activeTab === 'ongoing'
                ? (conversations.length === 0
                  ? t('playerMessagesPage.emptyNoConvos')
                  : search.trim()
                    ? t('playerMessagesPage.emptySearchOngoing')
                    : t('playerMessagesPage.emptyOngoingHint'))
                : (search.trim()
                  ? t('playerMessagesPage.emptySearchCompleted')
                  : t('playerMessagesPage.emptyCompleted'))}
            </EmptyState>
          )}
          {!conversationsLoading && listForTab.map((c) => (
            <ConversationItem
              key={c.id}
              $active={c.id === conversationId}
              onClick={() => navigate(`/player/messages/${c.id}`)}
            >
              <ConvRow>
                <ConvMeta>
                  <ConvName>{c.otherPlayer?.name || t('playerMessagesPage.chatFallback')}</ConvName>
                  {c.lastMessage && (
                    <ConvPreview>
                      {c.lastMessage.isMe ? t('playerMessagesPage.youPrefix') : ''}{c.lastMessage.body}
                    </ConvPreview>
                  )}
                  {c.updatedAt && (
                    <ConvTime>
                      {new Date(c.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </ConvTime>
                  )}
                </ConvMeta>
                {(c.unreadCount || 0) > 0 && (
                  <UnreadBadge title={t('playerMessagesPage.unreadTitle', { count: c.unreadCount })}>
                    {c.unreadCount > 99 ? '99+' : c.unreadCount}
                  </UnreadBadge>
                )}
                <HideThreadBtn
                  type="button"
                  title={t('playerMessagesPage.hideThreadTitle')}
                  aria-label={t('playerMessagesPage.hideThreadTitle')}
                  disabled={hideConversation.isLoading}
                  onClick={(e) => handleHideThread(e, c.id)}
                >
                  {t('playerMessagesPage.hideThread')}
                </HideThreadBtn>
              </ConvRow>
            </ConversationItem>
          ))}
        </ConversationList>
      </InboxSidebar>

      <ChatPanel>
        {conversationId ? (
          <>
            <ChatHeader>
              <ChatHeaderRow>
                <ChatTitle>
                  {activeConv?.otherPlayer?.name || t('playerMessagesPage.chatFallback')}
                  {isActiveCompleted && (
                    <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.8125rem', marginLeft: '0.5rem' }}>
                      {t('playerMessagesPage.matchCompletedReadOnly')}
                    </span>
                  )}
                </ChatTitle>
                <HideThreadBtn
                  type="button"
                  title={t('playerMessagesPage.hideThreadTitle')}
                  aria-label={t('playerMessagesPage.hideThreadTitle')}
                  disabled={hideConversation.isLoading}
                  onClick={(e) => handleHideThread(e, conversationId)}
                >
                  {t('playerMessagesPage.hideThread')}
                </HideThreadBtn>
              </ChatHeaderRow>
            </ChatHeader>
            <MessagesArea>
              {messagesLoading && (
                <LoadingBlock>{t('playerMessagesPage.loadingMessages')}</LoadingBlock>
              )}
              {!messagesLoading && messages.length === 0 && (
                <EmptyState>{t('playerMessagesPage.noMessagesYet')}</EmptyState>
              )}
              {!messagesLoading && messages.map((m) => (
                <MessageBubble key={m.id} $isMe={m.isMe}>
                  {m.body}
                  <MessageTime $isMe={m.isMe}>
                    {new Date(m.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                  </MessageTime>
                </MessageBubble>
              ))}
              <div ref={bottomRef} />
            </MessagesArea>
            {isActiveCompleted ? (
              <ReadOnlyNotice>{t('playerMessagesPage.readOnlyNotice')}</ReadOnlyNotice>
            ) : (
              <ChatForm onSubmit={handleSubmit}>
                <ChatInput
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('playerMessagesPage.placeholderMessage')}
                  maxLength={2000}
                />
                <SendBtn type="submit" disabled={sendMessage.isLoading || !body.trim()}>
                  {t('playerMessagesPage.send')}
                </SendBtn>
              </ChatForm>
            )}
          </>
        ) : (
          <EmptyState>
            {t('playerMessagesPage.emptySelectConversation')}
          </EmptyState>
        )}
      </ChatPanel>
    </PageWrap>
  );
}
